<script setup lang="ts">
import { useI18n } from '@/i18n';
import { useBillingStore } from '@/stores/billing';
import { Button, Input, LayoutGap, Modal } from '@dabble/quillui';
import { ref } from 'vue';

defineProps<{ disabled?: boolean; link?: boolean }>();

// Two-root template (Button + Modal) — forward parent attrs to the trigger Button explicitly.
defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const billingStore = useBillingStore();

const showModal = ref(false);
const code = ref('');
const submitting = ref(false);

function open() {
  code.value = '';
  showModal.value = true;
}

function cancel() {
  showModal.value = false;
}

async function apply() {
  const trimmed = code.value.trim();
  if (!trimmed) return;
  submitting.value = true;
  try {
    const ok = await billingStore.applyCouponCode(trimmed);
    if (ok) showModal.value = false;
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <Button
    v-bind="$attrs"
    :variant="link ? 'secondary' : 'primary'"
    :finish="link ? 'link' : undefined"
    :disabled="disabled"
    @click="open"
  >
    {{ t('billing_coupon_apply') }}
  </Button>

  <Modal v-model="showModal" :title="t('billing_coupon_apply_title')">
    <LayoutGap direction="column" size="medium">
      <Input v-model="code" :label="t('prompt_coupon_code')" type="text" @keydown.enter="apply" />
      <LayoutGap direction="row" size="small" align="right">
        <Button variant="secondary" finish="outlined" :disabled="submitting" @click="cancel">
          {{ t('cancel') }}
        </Button>
        <Button variant="primary" :disabled="submitting || !code.trim()" @click="apply">
          {{ t('billing_coupon_apply') }}
        </Button>
      </LayoutGap>
    </LayoutGap>
  </Modal>
</template>
